"use client";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthedApi } from "../../api/useAuthedApi";
import { usePortalScope } from "../../analytics/PortalScope";
import type { CustomMcpListing } from "../../api/customMcpApi";
import type {
  ApiKeyAuthMode,
  CreatedApiKey,
  CreateApiKeyInput,
} from "../../api/apiKeysApi";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the created key so the parent can show the reveal-once modal. */
  onCreated: (created: CreatedApiKey) => void;
}

/** Create an embed key. Allowlist is entered as comma-separated component names
 *  per kind — the backend trims anything the company hasn't installed
 *  (key.config ⊆ org install), so an over-broad entry is safely clamped. */
export function CreateEmbedKeyDialog({ open, onClose, onCreated }: Props) {
  const { apiKeys, customMcp } = useAuthedApi();
  const [name, setName] = useState("");
  const [authMode, setAuthMode] = useState<ApiKeyAuthMode>("backend_mint");
  // Grantable skills (org install ceiling) as a picker; empty selection = none.
  const [grantableSkills, setGrantableSkills] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  // Installed connectors to pick from (name = opaque c_<uuid>, label =
  // display_name). Empty selection = the key exposes NO connectors, which is
  // a valid (locked-down) embed.
  const [connectors, setConnectors] = useState<CustomMcpListing[]>([]);
  const [selectedMcp, setSelectedMcp] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    void customMcp
      .list("company")
      .then(setConnectors)
      .catch(() => setConnectors([]));
    void apiKeys
      .grantablePlugins()
      .then((c) => setGrantableSkills(c.skills ?? []))
      .catch(() => setGrantableSkills([]));
  }, [open, customMcp, apiKeys]);
  const [origins, setOrigins] = useState("");
  const [jwtIssuer, setJwtIssuer] = useState("");
  const [jwtJwksUrl, setJwtJwksUrl] = useState("");
  const [jwtAudience, setJwtAudience] = useState("");
  const [model, setModel] = useState("");
  const [rpm, setRpm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Both are portaled out of the page: the dialog, and the auth select's menu.
  const dialogScope = usePortalScope("key_create_dialog");
  const authMenuScope = usePortalScope("key_create_dialog-auth");

  const csv = (s: string) =>
    s.split(",").map((x) => x.trim()).filter(Boolean);

  const reset = () => {
    setName("");
    setAuthMode("backend_mint");
    setSelectedSkills(new Set());
    setSelectedMcp(new Set());
    setOrigins("");
    setJwtIssuer("");
    setJwtJwksUrl("");
    setJwtAudience("");
    setModel("");
    setRpm("");
    setError(null);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const components: Record<string, string[]> = {};
      if (selectedSkills.size) components.skills = [...selectedSkills];
      if (selectedMcp.size) components.mcp_servers = [...selectedMcp];
      const input: CreateApiKeyInput = {
        name: name.trim(),
        kind: "embed",
        config: Object.keys(components).length ? { components } : {},
        allowed_origins: csv(origins),
        auth_mode: authMode,
        model: model.trim() || null,
        rate_limit_rpm: rpm ? Number(rpm) : null,
      };
      if (authMode === "customer_jwt") {
        input.jwt_issuer = jwtIssuer.trim() || undefined;
        input.jwt_jwks_url = jwtJwksUrl.trim() || undefined;
        input.jwt_audience = jwtAudience.trim() || undefined;
      }
      const created = await apiKeys.create(input);
      reset();
      onCreated(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to create key");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: dialogScope }}
    >
      <DialogTitle>Create embed key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Support widget"
            autoFocus
          />

          <Divider textAlign="left">
            <Typography variant="caption">Capabilities (⊆ installed)</Typography>
          </Divider>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Skills
            </Typography>
            {grantableSkills.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No skills installed for this company.
              </Typography>
            ) : (
              <Stack sx={{ maxHeight: 160, overflowY: "auto", pl: 0.5 }}>
                {grantableSkills.map((s) => (
                  <FormControlLabel
                    key={s}
                    control={
                      <Checkbox
                        data-ph="select_skill"
                        size="small"
                        checked={selectedSkills.has(s)}
                        onChange={() =>
                          setSelectedSkills((prev) => {
                            const next = new Set(prev);
                            next.has(s) ? next.delete(s) : next.add(s);
                            return next;
                          })
                        }
                      />
                    }
                    label={<Typography variant="body2">{s}</Typography>}
                  />
                ))}
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" display="block">
              Pick which skills this key exposes. None selected = no skills.
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Connectors
            </Typography>
            {connectors.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No connectors installed for this company. The key will expose
                none.
              </Typography>
            ) : (
              <Stack sx={{ maxHeight: 180, overflowY: "auto", pl: 0.5 }}>
                {connectors.map((c) => (
                  <FormControlLabel
                    key={c.name}
                    control={
                      <Checkbox
                        data-ph="select_connector"
                        size="small"
                        checked={selectedMcp.has(c.name)}
                        onChange={() =>
                          setSelectedMcp((prev) => {
                            const next = new Set(prev);
                            next.has(c.name)
                              ? next.delete(c.name)
                              : next.add(c.name);
                            return next;
                          })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {c.display_name || c.name}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Pick which connectors this key can use. Leave all unchecked to
              expose none.
            </Typography>
          </Box>

          <Divider textAlign="left">
            <Typography variant="caption">Access</Typography>
          </Divider>
          <TextField
            label="Allowed origins (comma-separated)"
            size="small"
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder="https://acme.com, https://*.acme.com"
            helperText="Sites allowed to embed. Use * to wildcard a host (https://*.acme.com). Empty = server-to-server only."
          />
          <TextField
            select
            data-ph="auth-open"
            label="End-user auth"
            size="small"
            value={authMode}
            onChange={(e) => setAuthMode(e.target.value as ApiKeyAuthMode)}
            slotProps={{ select: { MenuProps: { slotProps: { list: authMenuScope } } } }}
          >
            <MenuItem data-ph="select_backend_mint" value="backend_mint">
              Backend-minted (email → token; recommended)
            </MenuItem>
            <MenuItem data-ph="select_customer_jwt" value="customer_jwt">
              Each user signs in (customer JWT)
            </MenuItem>
            <MenuItem data-ph="select_anonymous" value="anonymous">Anonymous visitors</MenuItem>
          </TextField>
          {authMode === "customer_jwt" && (
            <>
              <TextField
                label="JWT issuer"
                size="small"
                value={jwtIssuer}
                onChange={(e) => setJwtIssuer(e.target.value)}
                placeholder="https://auth.acme.com/"
              />
              <TextField
                label="JWKS URL"
                size="small"
                value={jwtJwksUrl}
                onChange={(e) => setJwtJwksUrl(e.target.value)}
                placeholder="https://auth.acme.com/.well-known/jwks.json"
              />
              <TextField
                label="JWT audience"
                size="small"
                value={jwtAudience}
                onChange={(e) => setJwtAudience(e.target.value)}
                placeholder="artie-embed"
              />
            </>
          )}

          <Divider textAlign="left">
            <Typography variant="caption">Limits (optional)</Typography>
          </Divider>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Model (blank = Auto)"
              size="small"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Rate limit (rpm)"
              size="small"
              type="number"
              value={rpm}
              onChange={(e) => setRpm(e.target.value)}
              sx={{ width: 160 }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-ph="cancel" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          data-ph="submit"
          variant="contained"
          onClick={submit}
          disabled={busy || !name.trim()}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Create key
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Reveal-once modal: shows the plaintext key + copy-ready embed snippets.
 *  The key is NEVER returned again by the API — this is the only chance to copy. */
export function RevealKeyDialog({
  created,
  apiBase,
  onClose,
}: {
  created: CreatedApiKey | null;
  apiBase: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const dialogScope = usePortalScope("key_reveal_dialog");
  if (!created) return null;
  const base = apiBase || window.location.origin;
  // SECURITY: only an `anonymous` key is safe to put in the browser (the key IS
  // the anon identity, legacy behaviour). For backend_mint / customer_jwt the
  // key is a SECRET that must stay on the server — the browser gets a per-user
  // JWT instead (minted server-side from the key). Never emit `#key=<secret>`
  // for those modes.
  const isAnon = created.auth_mode === "anonymous";
  const scriptSnippet = isAnon
    ? `<script src="${base}/embed/widget.js"\n` +
      `        data-embed-key="${created.key}"\n` +
      `        data-mode="bubble"></script>`
    : `<script src="${base}/embed/widget.js"\n` +
      `        data-jwt="<END_USER_JWT>"  <!-- mint per-user on YOUR backend; key stays server-side -->\n` +
      `        data-mode="bubble"></script>`;
  const iframeSnippet = isAnon
    ? `<iframe src="${base}/embed.html#key=${created.key}"\n` +
      `        style="border:0;width:400px;height:600px"></iframe>`
    : `<iframe src="${base}/embed.html#jwt=<END_USER_JWT>"\n` +
      `        style="border:0;width:400px;height:600px"></iframe>`;
  const linkSnippet = isAnon
    ? `${base}/embed.html#key=${created.key}`
    : `${base}/embed.html#jwt=<END_USER_JWT>`;

  const copy = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const Block = ({ label, text }: { label: string; text: string }) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Button size="small" data-ph="copy_snippet" onClick={() => copy(label, text)}>
          {copied === label ? "Copied" : "Copy"}
        </Button>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1,
          bgcolor: "action.hover",
          borderRadius: 1,
          fontSize: 12,
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {text}
      </Box>
    </Box>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper: dialogScope }}>
      <DialogTitle>New secret — copy it now</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">
            This is the only time the full key is shown. Store it securely — you
            won&apos;t be able to see it again.
          </Alert>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Chip size="small" label={created.name} />
              <Button size="small" data-ph="copy_key" onClick={() => copy("key", created.key)}>
                {copied === "key" ? "Copied" : "Copy key"}
              </Button>
            </Box>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                bgcolor: "action.hover",
                borderRadius: 1,
                fontSize: 13,
                overflowX: "auto",
                wordBreak: "break-all",
              }}
            >
              {created.key}
            </Box>
          </Box>
          <Divider textAlign="left">
            <Typography variant="caption">Embed snippets</Typography>
          </Divider>
          <Block label="Widget (script tag)" text={scriptSnippet} />
          <Block label="iframe" text={iframeSnippet} />
          <Block label="Shareable link" text={linkSnippet} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-ph="close" variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
